/* eslint-disable no-useless-escape */
/* eslint-disable max-len */
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import Sidenav from '@brands/Dashboard/Sidenav/Sidenav';
import { useWindowSize } from '@brands/hooks';
import { sendResetEmail } from '@brands/services/auth/sendResetEmail';
import { yupResolver } from '@hookform/resolvers/yup';
import TextButton from '@mui/material/Button';
import { isValidPhoneNumber } from 'libphonenumber-js';
import React, { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal from 'react-modal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

import Button from '../../Components/Button/Button';
import { customStylesForgotPassword } from '../../Utils/customStyles';
import { displayErrorDetails } from '../../Utils/displayError';
import styles from './styles.module.scss';
import leftArrow from './utils/icons/leftArrow.svg';

interface ForgotPasswordModalStepOneProps {
  forgotPasswordStep: number;
  setForgotPasswordStep: (num: number) => void;
  setUpdateEmailStep?: (num: number) => void;
  credentials: ForgotPasswordCredentials;
  setCredentials: (credentials: ForgotPasswordCredentials) => void;
}

type ForgotPasswordCredentials = {
  username?: string;
  code: string;
  new_password: string;
  reset_password_hash?: string;
};

type ForgotPasswordModalStepOneSubmitForm = {
  email?: string;
  phoneNumber?: string;
};

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .required('Email is required')
    .test('test-name', 'Enter Valid Email', (value: string | undefined) => {
      const emailRegex =
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
      const isValidEmail = emailRegex.test(value as string);
      const isValidPhone = phoneRegex.test(value as string);
      if (!isValidEmail && !isValidPhone) {
        return false;
      }
      return true;
    }),
});

const ForgotPasswordModalStepOne: FC<ForgotPasswordModalStepOneProps> = ({
  forgotPasswordStep,
  setForgotPasswordStep,
  setUpdateEmailStep,
  credentials,
  setCredentials,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const isMobile = useWindowSize().width < 992;
  const [searchParams] = useSearchParams();
  const [defaultUser, setDefaultUser] = useState<{ email: string } | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ForgotPasswordModalStepOneSubmitForm>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const closeModal = (): void => {
    setForgotPasswordStep(0);
    setCredentials({
      username: '',
      code: '',
      new_password: '',
    });
  };

  const startUpdateEmail = (): void => {
    closeModal();
    if (setUpdateEmailStep) {
      setUpdateEmailStep(1);
    }
  };

  useEffect(() => {
    const emailParam = searchParams.get('email');
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (emailParam) {
      const correctedEmail = emailParam.trim().replace(/\s/g, '+');
      const emailValid = emailRegex.test(correctedEmail);
      if (emailValid) {
        setValue('email', correctedEmail);
        setDefaultUser({ email: correctedEmail });
      }
    } else if (credentials?.username && emailRegex.test(credentials.username)) {
      setValue('email', credentials.username);
      setDefaultUser({ email: credentials.username });
    }
  }, [searchParams, setValue, credentials?.username]);

  const onSubmit = async (data: ForgotPasswordModalStepOneSubmitForm): Promise<void> => {
    try {
      setLoading(true);

      const email = data.email?.toLowerCase() as string;
      await sendResetEmail({ email });

      await setCredentials({
        ...credentials,
        username: isValidPhoneNumber(email) ? `+1${data.phoneNumber}` : email,
      });
      setForgotPasswordStep(5);
    } catch (error) {
      displayErrorDetails(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (forgotPasswordStep === 1) {
      document.body.classList.add('modal-open');
    } else if (document.body.classList.contains('modal-open') && forgotPasswordStep === 0) {
      document.body.classList.remove('modal-open');
    }
  }, [forgotPasswordStep]);
  return (
    <div className={styles.container}>
      <Sidenav isVisibleOnMobile hideDefaultOptions authLoog hideModals={() => setForgotPasswordStep(0)} />
      <Modal
        isOpen={forgotPasswordStep === 1}
        onRequestClose={closeModal}
        style={customStylesForgotPassword(isMobile, '40%')}
        contentLabel="Modal"
        ariaHideApp={false}
        className={`${styles.wrapper} ${styles.forgotPasswordModal}`}
        shouldCloseOnOverlayClick={false}
      >
        <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
          <span className={`${styles.formTitle} p-b-26`}>
            <span className={`${styles.formTitleForgot} fs-unmask`}>Reset your password</span>
            <div className={`text-center ${styles.formTitleLink} p-t-115 fs-unmask`}>
              <span className={`${styles.txt1} txt1`}>
                Enter your email address. We'll send you an email to reset your password.
              </span>
            </div>
          </span>
          {!defaultUser ? (
            <div className={styles.inputWrapper} data-validate="Valid email is: a@b.c">
              <TextField
                {...register('email')}
                label="Email"
                errors={errors.email ? errors : {}}
                name="email"
                onChange={(e) => {
                  setValue('email', e.target.value, { shouldValidate: true });
                }}
                inputClass={`${styles.profileInputClass} fs-exclude`}
                wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
              />
            </div>
          ) : (
            <div className={styles.inputWrapper} data-validate="Valid email is: a@b.c">
              <TextField
                {...register('email')}
                label="Email"
                errors={errors.email ? errors : {}}
                name="email"
                disabled
                inputClass={`${styles.profileInputClass} fs-exclude`}
                wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
              />
            </div>
          )}
          <div className={styles.buttonContainer}>
            <div className={styles.buttonWrapper}>
              <Button type="submit" className={styles.formBtn} disabled={loading}>
                Reset Password
              </Button>
            </div>
          </div>
          <div className={styles.accessToEmail} onClick={() => startUpdateEmail()}>
            No longer have access to your email?
          </div>
          <div className={`${styles.backArrow}`} style={{ marginTop: '0px' }}>
            <TextButton
              variant="text"
              onClick={() => {
                closeModal();
                navigate('/');
              }}
            >
              <img src={leftArrow} alt="left arrow" />
              <span>back to sign in</span>
            </TextButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

Modal.setAppElement('#root');

export default ForgotPasswordModalStepOne;
